import Loader from 'reusable/mobx-helpers/Loader'
import RouterStore from 'features/main/main-router/org-guard/RouterStore'
import { ObservableTime } from 'reusable/mobx-store-utils/ObservableTime'
import { makeAutoObservable } from 'mobx'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import StoreRef from 'reusable/mobx-store-utils/StoreRef'
import StepperFullscreenStore from 'features/gap-components/stepper/stepper-fullscreen/StepperFullscreenStore'
import { ProposalDto } from '@gap/backend/app-services/proposal/models/ProposalDto'
import { apiClient } from 'api/trpc/trpc-client'
import Debouncer from 'reusable/utils/Debouncer'
import { JobQueue } from "reusable/queue/JobQueue"
import { messageConfirm } from 'reusable/mui/modals/message-confirm'
import { ServiceStore } from 'features/services/ServiceStore'
import variables from 'variables/variables'
import { getDefaultValuesPerRateType, getServiceForStep } from '../proposalsUtils'
import { notNull } from '@gap/utils/utils'
import { defaultRateType } from '../proposalsConstants'
import ProposalsViewStore from '../ProposalsViewStore'
import { ServiceDto } from '@gap/backend/app-services/services/models/ServiceDto'
import { ServiceOption } from 'features/services/service-item/ServiceAutocomplete'
import { messageWarning } from 'reusable/mui/modals/message-warning'
import CacheStore from 'cache/CacheStore'
import OrgGuardStore from 'features/main/main-router/org-guard/OrgGuardStore'

interface ProposalsEditStoreProps {
   routerStore: RouterStore
   proposal: ProposalDto
   serviceStore: ServiceStore
   proposalsViewStore: ProposalsViewStore
   cacheStore: CacheStore
   orgGuardStore: OrgGuardStore
}

export default class ProposalsEditStore {
   sp
   loader = new Loader(false)
   cachedProposal: ProposalDto | null
   storeRef = new StoreRef<StepperFullscreenStore>()
   jobQueue = new JobQueue()

   autoSave = {
      debouncer: new Debouncer(),
      updateProposal: async (proposal?: ProposalDto) => {
         this.autoSave.debouncer.run(async () => {
            await this.jobQueue.run(() => this.updateProposal(proposal), 'Proposal changed', true)
         })
      }
   }

   constructor(sp: ProposalsEditStoreProps) {
      makeAutoObservable(this)
      this.sp = sp

      // TODO at some point
      // Also fetch data for step 4 and store in this store, so it's loaded when you get there

      // Fetch services in the background, so that it is loaded once you arrive at step 2
      this.fetchServices()
   }

   get org() {
      return this.sp.orgGuardStore.org
   }

   get appBarTitle() {
      if (!this.client?.name) {
         return 'Create proposal'
      }

      return `Send proposal to ${this.client.name}`
   }

   get lastSaved() {
      if (!this.proposal) {
         return ""
      }

      if (this.proposal && this.jobQueue.isRunning) {
         return 'Saving...'
      }

      return 'Last saved ' + ObservableTime.getInstance().renderRelativeTime(new Date(this.proposal.lastUpdatedAt))
   }

   get stepperFullscreenStore() {
      return this.storeRef.store
   }

   get services() {
      const [advisorServices, gapServices] = this.sp.serviceStore.services
         .reduce<[ServiceOption[], ServiceOption[]]>(([advisor, gap], sourceService) => {
            const service = JSON.parse(JSON.stringify(sourceService)) as ServiceDto
            const option: ServiceOption = {
               name: service.name,
               category: service.category,
               description: service.description,
               howItWorks: service.howItWorks,
               id: service.id,
               groupOption: `${service.orgId}-${service.category}`,
               orgId: service.orgId
            }
            if (service.orgId === variables.gapOrgId) {
               if (!gap.length || !gap.some(({ category }) => category !== option.category)) {
                  option.groupOption = `first${option.groupOption}`
               }
               gap.push(option)
            }
            else {
               if (!advisor.length || !advisor.some(({ category }) => category !== option.category)) {
                  option.groupOption = `first${option.groupOption}`
               }
               advisor.push(option)
            }
            return [advisor, gap]
         }, [[], []])
      return [...advisorServices, ...gapServices]
   }

   get client() {
      return this.sp.cacheStore.clientsCacheStore.clients.find(({ id }) => id === this.proposal.advisorClientId)
   }

   get proposal() {
      return this.sp.proposal
   }

   set proposal(value) {
      this.sp.proposal = value
   }

   fetchServices = async () => {
      await this.sp.serviceStore.fetch()

      // and active step is higher then step 2
      if (this.proposal.editingStepIndex > 1 && this.proposal.step2) {
         const foundServiceInStep2ThatDoesNotExistsAnymore = this.proposal.step2.some(stepService => {
            const service = this.sp.serviceStore.services.find(service => service.id === stepService.serviceId)
            return !service
         })

         if (foundServiceInStep2ThatDoesNotExistsAnymore) {
            // We found a service in step 2 that no longer exists
            await messageWarning('A service selected on step 2 was removed from your organisation.')
            this.goToStep2()
         }
      }
   }

   goToStep1 = () => { this.stepperFullscreenStore.activeStepIndex = 0 }
   goToStep2 = () => { this.stepperFullscreenStore.activeStepIndex = 1 }
   goToStep3 = () => { this.stepperFullscreenStore.activeStepIndex = 2 }
   goToStep4 = () => { this.stepperFullscreenStore.activeStepIndex = 3 }

   /** Note: This should only be used when needing to wait for the update. Otherwise, use autoSave.updateProposal(proposal) */
   updateProposal = async (proposal?: ProposalDto) => {
      // Reset signeeAmount if recipient length has changed
      if (this.proposal.step1 && this.proposal.step1.contacts && this.proposal.step4 && (this.proposal.step4.signeesAmount || 0) > this.proposal.step1.contacts.length) {
         this.proposal.step4.signeesAmount = this.proposal.step1.contacts.length
      }

      const { lastUpdatedAt } = await apiClient.proposals.updateProposal.mutate(proposal || this.proposal)
      if (proposal) {
         this.cachedProposal = JSON.parse(JSON.stringify(proposal)) as ProposalDto
         this.cachedProposal.lastUpdatedAt = lastUpdatedAt
      }
      this.proposal.lastUpdatedAt = lastUpdatedAt
   }

   onStepChange = async () => {
      /// await jobqueue to prevent race condition overriding proposal steps
      await this.jobQueue.promise
      if (this.cachedProposal) {
         this.proposal = this.cachedProposal
         this.cachedProposal = null
      }
      this.proposal.editingStepIndex = this.stepperFullscreenStore.activeStepIndex
      await this.autoSave.updateProposal()
   }

   navigateToProposals = async () => {
      if (this.jobQueue.isRunning) {
         if (await messageConfirm('You have unsaved changes, do you want to discard your changes and close anyway?', 'Discard changes?')) {
            this.sp.routerStore.goToNavPath('proposals')
         }
      } else {
         this.sp.routerStore.goToNavPath('proposals')
      }
   }

   // When a service gets removed from step 2, we need to ensure that the service is also removed from step 3
   deleteServiceFromStep2 = (serviceIdToRemove: string, servicesIsNowEmpty = false) => {
      if (!this.proposal.step3) {
         return // This should never occour
      }

      if (servicesIsNowEmpty) {
         this.proposal.step3 = []
      } else {
         this.proposal.step3 = this.proposal.step3.map(serviceRateType => {
            const services = serviceRateType.services.filter(({ serviceId }) => serviceId !== serviceIdToRemove)
            return {
               ...serviceRateType,
               services
            }
         }).filter(({ services }) => services.length)
      }
   }

   // When we add a service in step 2, we need to add it to step 3 aswell
   addServiceFromStep2 = (serviceIdToAdd: string) => {
      const service = notNull(this.sp.serviceStore.services.find(({ id }) => id === serviceIdToAdd))
      const startingPriceRate = service.pricing?.startingPriceRate || defaultRateType

      if (!this.proposal.step3) {
         this.proposal.step3 = [{
            services: [getServiceForStep(service)],
            ...getDefaultValuesPerRateType(startingPriceRate)
         }]
      } else {
         const step3RateType = this.proposal.step3.find(({ serviceRateType }) => serviceRateType === startingPriceRate)
         if (!step3RateType) {
            // The startingPriceRate does not yet exists in step3
            this.proposal.step3 = [
               ...this.proposal.step3,
               {
                  services: [getServiceForStep(service)],
                  ...getDefaultValuesPerRateType(startingPriceRate)
               }
            ]
         } else {
            step3RateType.services.push(getServiceForStep(service))
         }

      }
   }
}

export const { Provider: ProposalsEditStoreProvider, useProvidedStore: useProposalsEditStore } = createStoreContext(ProposalsEditStore)
